import { useMutation } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/authStore";
import { authApi } from "@/api/authApi";
import type { Conversation } from "@/types/conversationTypes";
import type {
  LoginInput,
  RegisterInput,
  AuthResponse,
  User,
} from "@/types/authTypes";
import { isAxiosError } from "axios";
import { forceDelay } from "@/lib/latency";
import { AUTH_SMOOTH_DELAY } from "@/lib/constants";

export const useLogin = (opts?: {
  onEmailNotConfirmed?: (email: string) => void;
  minDelayMs?: number;
}) => {
  const commit = useAuthStore((s) => s.login);
  const setUserDataLoading = useAuthStore((s) => s.setUserDataLoading);
  const setError = useAuthStore((s) => s.setError);

  const minDelay = opts?.minDelayMs ?? AUTH_SMOOTH_DELAY;

  return useMutation({
    mutationFn: async (input: LoginInput) => {
      setError(null);
      setUserDataLoading(true);

      const handleEmailNotConfirmed = async () => {
        await forceDelay(500);
        opts?.onEmailNotConfirmed?.(input.emailAddress);
      };

      try {
        const { data } = await authApi.login(input);

        if (data?.user && data.user.emailConfirmed === false) {
          await handleEmailNotConfirmed();
        }

        if (data?.token && data?.user) {
          await forceDelay(minDelay);
          commit(data as AuthResponse);
        }

        return data;
      } catch (err) {
        if (isAxiosError(err)) {
          const status = err.response?.status;
          const msg = (err.response?.data as any)?.message || "";
          if (status === 401 && /not confirmed/i.test(msg)) {
            await handleEmailNotConfirmed();
          }
          setError(typeof msg === "string" ? msg : "Login failed");
        } else {
          setError("Login failed");
        }
        throw err;
      } finally {
        await forceDelay(200);
        setUserDataLoading(false);
      }
    },
  });
};

export const useRegister = (opts?: {
  minDelayMs?: number;
  onNeedsEmailConfirmation?: (args: {
    emailAddress: string;
    token?: string;
  }) => void;
}) => {
  const setUserDataLoading = useAuthStore((s) => s.setUserDataLoading);
  const setError = useAuthStore((s) => s.setError);
  const minDelay = opts?.minDelayMs ?? AUTH_SMOOTH_DELAY;

  return useMutation({
    mutationFn: async (payload: RegisterInput) => {
      setError(null);
      setUserDataLoading(true);
      try {
        const { data } = await authApi.register(payload);
        await forceDelay(minDelay);
        opts?.onNeedsEmailConfirmation?.({
          emailAddress: payload.emailAddress,
          token: (data as any)?.emailConfirmationToken,
        });
        return data;
      } catch (err) {
        if (isAxiosError(err)) {
          const msg =
            (err.response?.data as any)?.message || "Registration failed";
          setError(typeof msg === "string" ? msg : "Registration failed");
        } else {
          setError("Registration failed");
        }
        throw err;
      } finally {
        await forceDelay(200);
        setUserDataLoading(false);
      }
    },
  });
};

export const useLogout = () => {
  const logout = useAuthStore((s) => s.logout);
  const setUserDataLoading = useAuthStore((s) => s.setUserDataLoading);
  return useMutation({
    mutationFn: async () => {
      setUserDataLoading(true);
      try {
        await forceDelay(400);
        logout();
      } finally {
        setUserDataLoading(false);
      }
    },
  });
};

export const useConfirmEmail = () =>
  useMutation({
    mutationFn: (token: string) => authApi.confirmEmail(token),
  });

export const useResendConfirmation = () =>
  useMutation({
    mutationFn: (email: string) => authApi.resendConfirmation(email),
  });

export const useForgotPassword = () =>
  useMutation({
    mutationFn: async (email: string) => {
      const { data } = await authApi.forgotPassword(email);
      return data;
    },
  });

export const useResetPassword = () =>
  useMutation({
    mutationFn: async (payload: {
      email: string;
      resetToken: string;
      newPassword: string;
    }) => {
      const { data } = await authApi.resetPassword(payload);
      return data;
    },
  });

export const useUpdateUser = () => {
  return useMutation({
    mutationFn: async (vars: {
      userId: string;
      data: Partial<
        Pick<User, "emailAddress" | "firstName" | "lastName" | "avatarUrl">
      >;
    }) => {
      const { data } = await authApi.updateUser(vars.userId, vars.data);
      return data.user;
    },
    onSuccess: (updatedUser) => {
      useAuthStore.getState().updateUser(updatedUser);
    },
  });
};

/**
 * Ștergere cont: verifică parola cu un login "silent" apoi șterge contul.
 * Necesită token în header (JwtAuthGuard), axios instance îl pune din Zustand.
 */
export const useDeleteAccount = (opts?: {
  minDelayMs?: number;
  requirePassword?: boolean;
}) => {
  const requirePassword = opts?.requirePassword ?? true;

  const logout = useAuthStore((s) => s.logout);
  const user = useAuthStore((s) => s.user);
  const setIsDeleting = useAuthStore((s) => s.setIsDeleting);
  const setError = useAuthStore((s) => s.setError);

  const minDelay = opts?.minDelayMs ?? AUTH_SMOOTH_DELAY;

  return useMutation({
    mutationFn: async (password?: string) => {
      if (!user) throw new Error("No current user.");
      setError(null);
      setIsDeleting(true);

      try {
        await forceDelay(minDelay);

        if (requirePassword) {
          if (!password) throw new Error("PASSWORD_REQUIRED");
          await authApi.login({ emailAddress: user.emailAddress, password });
        }

        await authApi.deleteUser(user.id);
        return true;
      } catch (err) {
        if (isAxiosError(err)) {
          const msg = (err.response?.data as any)?.message ?? "Delete failed";
          setError(typeof msg === "string" ? msg : "Delete failed");
        } else {
          setError((err as Error).message ?? "Delete failed");
        }
        throw err;
      } finally {
        setIsDeleting(false);
      }
    },
    onSuccess: () => {
      // logout on Success
      logout();
    },
  });
};

export const useGetUserConversations = () => {
  return useMutation<Conversation[], Error, string>({
    mutationFn: async (userId: string) => {
      const { data } = await authApi.getUserConversations(userId);
      return data;
    },
  });
};

export const useDeleteConversations = () => {
  const user = useAuthStore((s) => s.user);
  const setIsDeleting = useAuthStore((s) => s.setIsDeleting);

  return useMutation<void, Error, string>({
    mutationFn: async (password: string) => {
      if (!user) throw new Error("No current user.");
      setIsDeleting(true);
      try {
        await authApi.deleteConversations(user.id, password);
      } finally {
        setIsDeleting(false);
      }
    },
  });
};

export const useGetUserActiveConversation = () => {
  return useMutation<Conversation | null, Error, string>({
    mutationFn: async (userId: string) => {
      const { data } = await authApi.getUserActiveConversation(userId);
      return data;
    },
  });
};

export const useAttachConversationToUser = () => {
  return useMutation<void, Error, { conversationId: string; userId: string }>({
    mutationFn: async ({ conversationId, userId }) => {
      await authApi.attachConversationToUser(conversationId, userId);
    },
  });
};
